import PropTypes from 'prop-types';
import { useState, useCallback } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Dialog from '@mui/material/Dialog';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import InputAdornment from '@mui/material/InputAdornment';
import ListItemButton, { listItemButtonClasses } from '@mui/material/ListItemButton';
import Iconify from 'src/components/iconify';
import SearchNotFound from 'src/components/search-not-found';
import { Avatar, ListItemText, TableCell } from '@mui/material';
import { ASSETS_API } from 'src/config-global';

// ----------------------------------------------------------------------

export default function DishByCategoryListDialog({
  title = 'Lista de Pratos',
  list,
  action,
  //
  open,
  onClose,
  //
  selected,
  onSelect,
  data
}) {
  const [searchAddress, setSearchAddress] = useState('');

  const dataFiltered = applyFilter({
    inputData: list,
    query: searchAddress,
  });

  const notFound = !dataFiltered.length && !!searchAddress;

  const handleSearchAddress = useCallback((event) => {
    setSearchAddress(event.target.value);
  }, []);

  const handleSelectAddress = useCallback(
    (address) => {
      onSelect(address);
      setSearchAddress('');
      onClose();
    },
    [onClose, onSelect]
  );

  const renderList = (
    <Stack
      spacing={0.5}
      sx={{
        p: 0.5,
        maxHeight: 80 * 8,
        overflowX: 'hidden',
      }}
    >
      {dataFiltered.map((dish) => (
        <Stack
          key={dish.id}
          spacing={0.5}
          component={ListItemButton}
          selected={selected(`${dish.id}`)}
          onClick={() => handleSelectAddress(dish)}
          sx={{
            py: 1,
            px: 1.5,
            borderRadius: 1,
            flexDirection: 'column',
            alignItems: 'flex-start',
            [`&.${listItemButtonClasses.selected}`]: {
              bgcolor: 'action.selected',
              '&:hover': {
                bgcolor: 'action.selected',
              },
            },
          }}
        >
           <TableCell sx={{ display: 'flex', alignItems: 'center' }}>
          <Avatar
            alt={dish.portugueseName}
            variant="rounded"
            src={`${ASSETS_API}${dish.photo}`}
            sx={{ width: 54, height: 54, mr: 2 }}
          />

          <ListItemText
            disableTypography
            primary={dish.portugueseName}
            secondary={
              <Box component="div" sx={{ typography: 'body2', color: 'text.disabled' }}>
                <b>Preço:</b> {dish.cost} €
              </Box>
            }
          />
        </TableCell>
        </Stack>
      ))}
    </Stack>
  );

  return (
    <Dialog fullWidth maxWidth="xs" open={open} onClose={onClose}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ p: 3, pr: 1.5 }}
      >
        <Typography variant="h6"> {title} </Typography>

        {action && action}
      </Stack>

      <Stack sx={{ p: 2, pt: 0 }}>
        <TextField
          value={searchAddress}
          onChange={handleSearchAddress}
          placeholder="Search..."
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Iconify icon="eva:search-fill" sx={{ color: 'text.disabled' }} />
              </InputAdornment>
            ),
          }}
        />
      </Stack>

      {notFound ? (
        <SearchNotFound query={searchAddress} sx={{ px: 3, pt: 5, pb: 10 }} />
      ) : (
        renderList
      )}
    </Dialog>
  );
}

DishByCategoryListDialog.propTypes = {
  action: PropTypes.node,
  list: PropTypes.array,
  onClose: PropTypes.func,
  onSelect: PropTypes.func,
  open: PropTypes.bool,
  selected: PropTypes.func,
  title: PropTypes.string,
  data: PropTypes.object
};

// ----------------------------------------------------------------------

function applyFilter({ inputData, query }) {
  if (query) {
    return inputData.filter(
      (dish) =>
      dish.portugueseName.toLowerCase().indexOf(query.toLowerCase()) !== -1 ||
      dish.cost.toString().toLowerCase().indexOf(query.toLowerCase()) !== -1 
    );
  }

  return inputData;
}
